import type { App } from 'vue';
import { createPinia } from 'pinia';
import {
	Caller,
	Realtime,
	REALTIME_KEY,
	grantedPlugin,
	phoneInputPlugin,
	piniaExtraPlugin,
	registerCaller,
	subscribeCallerError,
	useTokenStore,
	useToastStore,
	vFormPlugin,
} from '@common/index';
import router from '@/router';
import i18n from './i18n';
import vuetify from './vuetify';

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:4000';
export const REALTIME_URL = import.meta.env.VITE_REALTIME_URL ?? API_BASE_URL;

export const pinia = createPinia();

/**
 * Canal temps réel du back-office.
 *
 * Il alimente la carte de la flotte et le flux des courses. L'adhésion au salon
 * de supervision est arbitrée par l'API : sans le droit `BO_LIVE_MAP`, la
 * demande est refusée côté serveur.
 */
export const realtime = new Realtime({
	url: REALTIME_URL,
	path: '/realtime',
	getToken: () => useTokenStore(pinia).token?.accessToken ?? null,
	// Interrogation plus rapprochée qu'en mobilité : un poste d'exploitation est
	// sur réseau fixe, et la supervision doit rester réactive.
	pollingIntervalMs: 4000,
});

export function registerPlugins(app: App): void {
	app.use(pinia);

	registerCaller(
		'api',
		(activePinia) => new Caller(`${API_BASE_URL}/api`, activePinia, { useAuth: true }),
	);

	app.use(piniaExtraPlugin, { pinia, router, apiBaseUrl: `${API_BASE_URL}/api` });
	app.use(i18n);
	app.use(vuetify);
	app.use(router);
	app.use(grantedPlugin, { pinia, router });
	app.use(vFormPlugin);
	// Après Vuetify : le champ téléphone monte un `VSelect` et un `VTextField`.
	app.use(phoneInputPlugin);

	const toasts = useToastStore(pinia);
	subscribeCallerError((error) => {
		toasts.error({ message: error?.message });
	});

	app.provide(REALTIME_KEY, realtime);
}

export { router };
